import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { formatIDR, formatDateID, formatTimeID } from "@/lib/format";
import {
  ChevronLeft, ChevronDown, ChevronRight, Landmark, QrCode, Upload, Star, AlertTriangle,
  CheckCircle2, Clock, Copy, Truck, MapPin, RotateCcw, MessageCircle, Headset, Store,
} from "lucide-react";
import { toast } from "sonner";

const WA_CS = "6281234567890";
const PAY_LABEL = { manual_transfer: "Transfer Bank", manual_qris: "QRIS Kiyana", manual: "Transfer Bank / QRIS", xendit_va: "Virtual Account", cod: "COD (Bayar di Tempat)" };
const STEP_LABEL = {
  perlu_diproses: "Pembayaran terkonfirmasi. Pesanan menunggu diproses penjual.",
  pembayaran_diverifikasi: "Pembayaran terkonfirmasi. Pesanan menunggu diproses penjual.",
  diproses: "Pesanan sedang diproses penjual.",
  dikemas: "Pesanan sedang dikemas.",
  siap_dikirim: "Pesanan siap dikirim, menunggu kurir.",
  dikirim: "Pesanan dalam proses pengantaran.",
  diterima: "Paket sudah diterima. Terima kasih!",
  selesai: "Pesanan selesai.",
};

function etdMaxDays(etd) {
  if (!etd) return 3;
  if (etd.includes("hari ini")) return 0;
  const m = etd.match(/(\d+)\s*-\s*(\d+)/);
  if (m) return +m[2];
  const m2 = etd.match(/(\d+)/);
  return m2 ? +m2[0] : 3;
}

function addDaysFmt(iso, days) {
  try {
    const d = new Date(iso);
    d.setDate(d.getDate() + days);
    return d.toLocaleDateString("id-ID", { day: "numeric", month: "short", timeZone: "Asia/Jakarta" });
  } catch { return "-"; }
}

function ReviewForm({ productId, orderId, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, comment, order_id: orderId });
      toast.success("Terima kasih atas ulasanmu!");
      onDone();
    } catch (e) { toast.error(extractError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 bg-blush/50 rounded-xl p-3">
      <div className="flex gap-1 mb-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`review-star-${n}`} onClick={() => setRating(n)}>
            <Star className={`w-6 h-6 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-cocoa-muted"}`} />
          </button>
        ))}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Ceritakan pengalamanmu..." className="w-full px-3 py-2 rounded-xl border border-blush-soft text-xs focus:outline-none focus:ring-2 ring-brand" />
      <button data-testid="review-submit" onClick={submit} disabled={busy} className="mt-2 h-9 px-5 rounded-full bg-brand text-white text-xs font-semibold disabled:opacity-50">
        {busy ? "Mengirim..." : "Kirim Ulasan"}
      </button>
    </div>
  );
}

export default function StoreOrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [o, setO] = useState(null);
  const [err, setErr] = useState(false);
  const [form, setForm] = useState({ bank_name: "BCA", sender_name: "", paid_at: new Date().toISOString().slice(0, 10) });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [returMode, setReturMode] = useState(false);
  const [returReason, setReturReason] = useState("");
  const [returFile, setReturFile] = useState(null);
  const [resiCourier, setResiCourier] = useState("jne");
  const [resiAwb, setResiAwb] = useState("");
  const [reviewFor, setReviewFor] = useState(null);
  const [expandTotal, setExpandTotal] = useState(false);
  const [expandInfo, setExpandInfo] = useState(false);
  const [trackOpen, setTrackOpen] = useState(false);
  const [trackData, setTrackData] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonCode, setCancelReasonCode] = useState("change_address");
  const [cancelReasons, setCancelReasons] = useState([]);
  useEffect(() => { api.get("/meta/cancel-reasons").then((r) => setCancelReasons(r.data.options)).catch(() => {}); }, []);
  const fileRef = useRef();
  const returFileRef = useRef();

  const load = () => api.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); }, [id]);

  const loadTrack = async () => {
    if (!o?.shipping_info?.awb) return;
    setTrackLoading(true);
    try { const r = await api.post("/shipping/track", { awb: o.shipping_info.awb, courier: o.shipping_info.courier_code || "jne" }); setTrackData(r.data); }
    catch (e) { toast.error(extractError(e)); } finally { setTrackLoading(false); }
  };
  useEffect(() => { if (trackOpen && !trackData) loadTrack(); }, [trackOpen]);

  if (err) return <div className="py-20 text-center text-sm text-cocoa-muted">Pesanan tidak ditemukan.</div>;
  if (!o) return <div className="py-20 text-center text-sm text-cocoa-muted">Memuat...</div>;

  const latestProof = o.proofs?.[0];
  const needPay = ["menunggu_pembayaran_manual", "menunggu_pembayaran_xendit", "bukti_ditolak"].includes(o.payment_status);
  const canRetur = ["dikirim", "diterima", "selesai"].includes(o.order_status);
  const canReview = o.order_status === "selesai";
  const canCancel = ["menunggu_pembayaran_manual", "menunggu_pembayaran_xendit", "cod_menunggu_verifikasi"].includes(o.payment_status);
  const cr = o.cancel_request || {};
  const canRequestCancel = o.payment_status === "sudah_dibayar" && ["pembayaran_diverifikasi", "perlu_diproses", "diproses", "dikemas", "siap_dikirim", "dikirim"].includes(o.order_status) && cr.status !== "menunggu_persetujuan";
  const si = o.shipping_info || {};
  const inDelivery = ["perlu_diproses", "diproses", "dikemas", "siap_dikirim", "dikirim"].includes(o.order_status);
  const estimasi = addDaysFmt(o.created_at, etdMaxDays(si.etd));
  const garansi = addDaysFmt(o.created_at, etdMaxDays(si.etd) + 2);

  const submitProof = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Unggah foto bukti transfer dulu");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/payments/manual", {
        order_id: o.id, bank_name: form.bank_name, sender_name: form.sender_name,
        amount: o.total, paid_at: form.paid_at, proof_url: up.data.url,
      });
      toast.success("Bukti pembayaran terkirim, menunggu verifikasi");
      setFile(null);
      load();
    } catch (e2) { toast.error(extractError(e2)); } finally { setBusy(false); }
  };

  const submitRetur = async () => {
    if (!returReason.trim()) return toast.error("Isi alasan retur");
    if (!returFile) return toast.error("Wajib lampirkan bukti foto atau video");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", returFile);
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/orders/${o.id}/retur`, { reason: returReason, proof_url: up.data.url });
      toast.success("Pengajuan retur terkirim");
      setReturMode(false); setReturFile(null); setReturReason("");
      load();
    } catch (e) { toast.error(extractError(e)); } finally { setBusy(false); }
  };

  const submitReturResi = async () => {
    if (resiAwb.trim().length < 4) return toast.error("Nomor resi tidak valid");
    try {
      await api.post(`/orders/${o.id}/retur-resi`, { courier: resiCourier, awb: resiAwb.trim() });
      toast.success("Resi pengembalian tersimpan");
      setResiAwb("");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const cancelOrder = async () => {
    if (canRequestCancel) { setCancelMode(true); scrollTo("cancel-section"); return; }
    if (!window.confirm("Yakin batalkan pesanan ini?")) return;
    try {
      await api.post(`/orders/${o.id}/cancel`);
      toast.success("Pesanan dibatalkan");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const submitCancelRequest = async () => {
    if (cancelReasonCode === "others" && cancelReason.trim().length < 5) return toast.error("Alasan pembatalan wajib diisi (min. 5 karakter)");
    setBusy(true);
    try {
      await api.post(`/orders/${o.id}/cancel`, { reason: cancelReason.trim(), reason_code: cancelReasonCode });
      toast.success("Pengajuan pembatalan terkirim, menunggu persetujuan admin");
      setCancelMode(false);
      setCancelReason("");
      load();
    } catch (e) { toast.error(extractError(e)); } finally { setBusy(false); }
  };

  const pickReturFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const isVid = f.type.startsWith("video/");
    const limit = isVid ? 25 * 1024 * 1024 : 5 * 1024 * 1024;
    if (f.size > limit) {
      toast.error(isVid ? "Video maksimal 25MB" : "Foto maksimal 5MB");
      e.target.value = "";
      return;
    }
    setReturFile(f);
  };

  const scrollTo = (id_) => document.getElementById(id_)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const goLacak = () => { setTrackOpen(true); scrollTo("info-pengiriman"); };

  return (
    <div className="space-y-3 animate-fade-up pb-24">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button data-testid="detail-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white border border-blush-soft flex items-center justify-center">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-semibold">Rincian Pesanan</h1>
        <div className="w-9" />
      </div>

      {/* Banner estimasi tiba */}
      {(si.etd || si.awb) && inDelivery && (
        <div className="rounded-2xl overflow-hidden">
          <div className="bg-teal-600 text-white px-4 py-3 text-sm font-medium" data-testid="estimasi-banner">
            Estimasi tiba: {estimasi}
          </div>
          <div className="bg-white px-4 py-2.5 text-[11px] text-cocoa-muted flex gap-2 border-x border-b border-blush-soft rounded-b-2xl">
            <Clock className="w-3.5 h-3.5 text-teal-600 flex-shrink-0 mt-0.5" />
            <span>Garansi Tiba: dapatkan voucher Rp2.000 jika pesanan belum tiba {garansi}. <span className="text-brand">Selengkapnya</span></span>
          </div>
        </div>
      )}

      {/* Info Pengiriman */}
      {(si.service_name || si.awb || o.courier) && (
        <div id="info-pengiriman" className="bg-white rounded-2xl border border-blush-soft overflow-hidden">
          <button data-testid="tracking-toggle" onClick={() => setTrackOpen(!trackOpen)} className="w-full p-4 text-left">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Info Pengiriman</div>
              {si.awb && <ChevronRight className={`w-4 h-4 text-cocoa-muted transition ${trackOpen ? "rotate-90" : ""}`} />}
            </div>
            <div className="text-xs text-cocoa-muted mt-1">
              {si.service_name || o.courier}{si.awb ? `: ${si.awb}` : ""}
            </div>
            <div className="flex gap-2 mt-2.5">
              <Truck className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-xs text-teal-700 font-medium">{STEP_LABEL[o.order_status] || "Pesanan sedang disiapkan."}</div>
                <div className="text-[10px] text-cocoa-muted">{o.shipped_at ? `${formatDateID(o.shipped_at)} ${formatTimeID(o.shipped_at)}` : `${formatDateID(o.created_at)}`}</div>
              </div>
            </div>
          </button>
          {trackOpen && (
            <div className="px-4 pb-4 border-t border-blush-soft pt-3">
              {trackLoading && <div className="text-xs text-cocoa-muted py-3 text-center">Melacak resi...</div>}
              {!si.awb && <div className="text-xs text-cocoa-muted py-2">Resi belum diterbitkan — kurir akan menjemput paketmu.</div>}
              {trackData && (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${trackData.delivered ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                      {trackData.delivered ? "Terkirim" : "Dalam Pengiriman"}
                    </span>
                    {trackData.mock && <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">Simulasi</span>}
                    <button data-testid="tracking-refresh" onClick={loadTrack} className="ml-auto text-[11px] text-brand font-medium">Refresh</button>
                  </div>
                  {trackData.events.map((e, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full mt-1 ${i === 0 ? "bg-brand" : "bg-blush border border-blush-soft"}`} />
                        {i < trackData.events.length - 1 && <div className="w-0.5 flex-1 bg-blush" />}
                      </div>
                      <div className="pb-4">
                        <div className={`text-xs ${i === 0 ? "font-semibold text-cocoa" : "text-cocoa-muted"}`}>{e.status}</div>
                        <div className="text-[10px] text-cocoa-muted">{e.date}{e.location ? ` • ${e.location}` : ""}</div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Penilaian — CTA besar tepat di bawah tracking resi */}
      {canReview && (
        <div data-testid="review-cta-card" className="bg-white rounded-2xl border-2 border-brand/30 p-4">
          <div className="text-sm font-bold flex items-center gap-2"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> Pesanan selesai — yuk beri penilaian!</div>
          <div className="text-[11px] text-cocoa-muted mt-0.5">Penilaianmu membantu pembeli lain memilih produk yang tepat.</div>
          {o.items.map((it, i) => (
            o.my_reviews?.[it.product_id] != null ? (
              <div key={i} className="mt-2.5 flex items-center gap-3 bg-blush/40 rounded-xl p-2.5">
                <img src={it.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium line-clamp-1">{it.name}</div>
                  <div className="text-[11px] text-emerald-600 font-semibold mt-0.5">Sudah diulas {"★".repeat(o.my_reviews[it.product_id])}</div>
                </div>
                <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              </div>
            ) : (
              <div key={i} className="mt-2.5">
                {reviewFor === it.product_id ? (
                  <ReviewForm productId={it.product_id} orderId={o.id} onDone={() => { setReviewFor(null); load(); }} />
                ) : (
                  <button data-testid={`review-cta-${i}`} onClick={() => setReviewFor(it.product_id)} className="w-full flex items-center gap-3 bg-blush/50 hover:bg-blush rounded-xl p-3 text-left border border-brand/20 transition">
                    <img src={it.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium line-clamp-1">{it.name}</div>
                      <div className="text-[10px] text-cocoa-muted">{it.variant} • x{it.qty}</div>
                    </div>
                    <span className="flex-shrink-0 h-10 px-5 rounded-full bg-brand text-white text-sm font-bold flex items-center gap-1.5"><Star className="w-4 h-4 fill-white" /> Beri Ulasan</span>
                  </button>
                )}
              </div>
            )
          ))}
        </div>
      )}

      {/* Alamat Pengiriman */}
      <div className="bg-white rounded-2xl border border-blush-soft p-4">
        <div className="text-sm font-semibold mb-2">Alamat Pengiriman</div>
        <div className="flex gap-2.5">
          <MapPin className="w-4 h-4 text-cocoa-muted flex-shrink-0 mt-0.5" />
          <div className="text-xs leading-relaxed">
            {o.destination?.name ? (
              <>
                <div className="font-semibold text-cocoa">{o.destination.name} <span className="font-normal text-cocoa-muted">({o.destination.phone})</span></div>
                <div className="text-cocoa-muted">{o.destination.detail}, {o.destination.village}, {o.destination.district}, {o.destination.city}, {o.destination.province}</div>
              </>
            ) : (
              <div className="text-cocoa-muted whitespace-pre-line">{o.address || "-"}</div>
            )}
          </div>
        </div>
      </div>

      {/* Produk + Total */}
      <div className="bg-white rounded-2xl border border-blush-soft overflow-hidden">
        <div className="px-4 pt-3 pb-2 flex items-center gap-2 border-b border-blush-soft">
          <span className="text-[9px] bg-brand text-white font-bold px-1.5 py-0.5 rounded">Official</span>
          <Store className="w-3.5 h-3.5 text-cocoa-muted" />
          <span className="text-xs font-semibold">KIYANA by Cirebon Cloth</span>
        </div>
        <div className="px-4">
          {o.items.map((it, i) => (
            <div key={i} className="py-3 border-b border-blush-soft last:border-0">
              <div className="flex gap-3">
                <img src={it.image} alt="" className="w-16 h-16 rounded-xl object-cover border border-blush-soft" />
                <div className="flex-1 min-w-0">
                  <Link to={`/store/product/${it.product_id}`} className="text-sm font-medium line-clamp-1">{it.name}</Link>
                  <div className="flex justify-between items-center mt-0.5">
                    <span className="text-[11px] text-cocoa-muted">{it.variant || "All Size"}</span>
                    <span className="text-[11px] text-cocoa-muted">x{it.qty}</span>
                  </div>
                  <div className="text-right mt-1">
                    <span className="text-brand font-bold text-sm">{formatIDR(it.price)}</span>
                  </div>
                </div>
              </div>
              {canReview && (
                o.my_reviews?.[it.product_id] != null
                  ? <span data-testid={`reviewed-${i}`} className="mt-1.5 inline-block text-[11px] text-emerald-600 font-medium">Sudah diulas {"★".repeat(o.my_reviews[it.product_id])}</span>
                  : reviewFor === it.product_id
                    ? <ReviewForm productId={it.product_id} orderId={o.id} onDone={() => { setReviewFor(null); load(); }} />
                    : <button data-testid={`review-open-${i}`} onClick={() => setReviewFor(it.product_id)} className="mt-1.5 text-xs text-brand font-semibold">Beri Ulasan ★</button>
              )}
            </div>
          ))}
        </div>
        <button data-testid="rincian-total-toggle" onClick={() => setExpandTotal(!expandTotal)} className="w-full px-4 py-3 flex items-center justify-center gap-2 border-t border-blush-soft">
          <span className="text-sm">Total Pesanan: <b className="text-cocoa">{formatIDR(o.total)}</b></span>
          <ChevronDown className={`w-4 h-4 text-cocoa-muted transition ${expandTotal ? "rotate-180" : ""}`} />
        </button>
        {expandTotal && (
          <div className="px-4 pb-4 pt-1 space-y-1.5 text-xs border-t border-blush-soft">
            <div className="flex justify-between pt-2"><span className="text-cocoa-muted">Subtotal Produk</span><span>{formatIDR(o.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-cocoa-muted">Ongkos Kirim{si.service_name ? ` (${si.service_name})` : ""}</span><span>{o.shipping === 0 ? "Gratis" : formatIDR(o.shipping)}</span></div>
            {si.cost > o.shipping && <div className="flex justify-between text-emerald-600"><span>Subsidi ongkir</span><span>-{formatIDR(si.cost - o.shipping)}</span></div>}
            {o.discount > 0 && <div className="flex justify-between text-emerald-600"><span>Voucher {o.voucher_code}</span><span>-{formatIDR(o.discount)}</span></div>}
            {o.cod_fee > 0 && <div className="flex justify-between"><span className="text-cocoa-muted">Fee COD ({o.cod_fee_percent}%)</span><span>{formatIDR(o.cod_fee)}</span></div>}
            <div className="flex justify-between font-bold pt-2 border-t border-blush-soft text-sm"><span>Total Pesanan</span><span className="text-brand">{formatIDR(o.total)}</span></div>
          </div>
        )}
      </div>

      {/* Pembayaran (belum bayar) */}
      {o.payment_status === "bukti_ditolak" && latestProof?.rejection_reason && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-3.5 flex gap-2.5">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div className="text-xs text-red-700"><b>Bukti pembayaran ditolak.</b> Alasan: {latestProof.rejection_reason}. Silakan unggah ulang bukti yang benar di bawah.</div>
        </div>
      )}
      {needPay && (
        <div id="payment-section" className="bg-white rounded-2xl border border-blush-soft p-4 space-y-3">
          <div className="text-sm font-semibold">Selesaikan Pembayaran</div>
          <div className="bg-blush rounded-xl p-3 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Landmark className="w-4 h-4 text-brand" /> BCA • a.n. Kiyana by Cirebon Cloth</span>
              <button onClick={() => { navigator.clipboard?.writeText("8830123456"); toast.success("No. rekening disalin"); }} className="flex items-center gap-1 text-brand font-mono font-semibold">8830123456 <Copy className="w-3 h-3" /></button>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Landmark className="w-4 h-4 text-brand" /> Mandiri • a.n. Kiyana by Cirebon Cloth</span>
              <button onClick={() => { navigator.clipboard?.writeText("1400099887766"); toast.success("No. rekening disalin"); }} className="flex items-center gap-1 text-brand font-mono font-semibold">1400099887766 <Copy className="w-3 h-3" /></button>
            </div>
            <div className="flex items-center gap-2 pt-1 border-t border-blush-soft">
              <QrCode className="w-4 h-4 text-brand" /> QRIS Kiyana tersedia di kasir toko / minta via CS WhatsApp
            </div>
          </div>
          <div className="text-center text-sm">Total tagihan: <b className="text-brand text-lg">{formatIDR(o.total)}</b></div>
          <form onSubmit={submitProof} className="space-y-2.5">
            <select data-testid="proof-bank" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-blush-soft text-sm bg-white">
              <option>BCA</option><option>Mandiri</option><option>BRI</option><option>BNI</option><option>QRIS</option>
            </select>
            <input data-testid="proof-sender" value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} placeholder="Nama pemilik rekening pengirim" required className="w-full h-11 px-3 rounded-xl border border-blush-soft text-sm focus:outline-none focus:ring-2 ring-brand" />
            <input data-testid="proof-date" type="date" value={form.paid_at} onChange={(e) => setForm({ ...form, paid_at: e.target.value })} required className="w-full h-11 px-3 rounded-xl border border-blush-soft text-sm focus:outline-none focus:ring-2 ring-brand" />
            <button type="button" data-testid="proof-file-btn" onClick={() => fileRef.current.click()} className="w-full h-20 rounded-xl border-2 border-dashed border-blush-soft flex flex-col items-center justify-center gap-1 text-cocoa-muted">
              <Upload className="w-5 h-5" />
              <span className="text-[11px]">{file ? file.name : "Unggah foto bukti transfer (JPG/PNG, maks 5MB)"}</span>
            </button>
            <input ref={fileRef} data-testid="proof-file" type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
            <button data-testid="proof-submit" disabled={busy} className="w-full h-12 rounded-full bg-brand text-white text-sm font-semibold disabled:opacity-50">
              {busy ? "Mengirim..." : "Kirim Bukti Pembayaran"}
            </button>
          </form>
        </div>
      )}
      {o.payment_status === "menunggu_verifikasi_manual" && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 text-xs text-amber-700 flex gap-2">
          <Clock className="w-4 h-4 flex-shrink-0" /> Bukti pembayaranmu sedang diverifikasi admin (maks. 1x24 jam).
        </div>
      )}
      {o.payment_status === "cod_menunggu_verifikasi" && (
        <div data-testid="cod-pending-banner" className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 text-xs text-amber-700 flex gap-2">
          <Clock className="w-4 h-4 flex-shrink-0" /> Pengajuan COD-mu sedang diverifikasi admin (formulir perjanjian + KTP/Paspor). Pesanan diproses setelah disetujui.
        </div>
      )}
      {o.payment_status === "cod_belum_terbayar" && (
        <div data-testid="cod-active-banner" className="bg-blue-50 border border-blue-200 rounded-2xl p-3.5 text-xs text-blue-700 flex gap-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> COD disetujui — siapkan uang tunai <b>{formatIDR(o.total)}</b> (termasuk fee COD {formatIDR(o.cod_fee || 0)}) untuk dibayar ke kurir saat paket tiba.
        </div>
      )}
      {o.payment_status === "cod_ditolak" && (
        <div data-testid="cod-rejected-banner" className="bg-red-50 border border-red-200 rounded-2xl p-3.5 text-xs text-red-700 flex gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> Pengajuan COD ditolak{o.cod_rejected_note ? `: ${o.cod_rejected_note}` : ""}
        </div>
      )}
      {o.payment_status === "cod_ditolak_pembeli" && (
        <div data-testid="cod-refused-banner" className="bg-red-50 border border-red-200 rounded-2xl p-3.5 text-xs text-red-700 flex gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> Paket COD ditolak saat pengiriman. Akun Anda tidak dapat menggunakan metode COD lagi — silakan gunakan Transfer/QRIS/VA untuk pesanan berikutnya.
        </div>
      )}
      {o.payment_status === "sudah_dibayar" && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3.5 text-xs text-emerald-700 flex gap-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> Pembayaran sudah diverifikasi. Terima kasih!
        </div>
      )}

      {/* Butuh Bantuan */}
      <div className="bg-white rounded-2xl border border-blush-soft p-4">
        <div className="text-sm font-semibold mb-1">Butuh Bantuan?</div>
        {[
          { icon: RotateCcw, label: "Ajukan Pengembalian", testid: "bantuan-retur", action: () => { if (canRetur) { setReturMode(true); scrollTo("retur-section"); } else toast.info("Retur hanya untuk pesanan berstatus dikirim/selesai"); } },
          { icon: MessageCircle, label: "Hubungi Penjual", testid: "bantuan-hubungi", action: () => { localStorage.setItem("kiyana_chat_draft", `Halo kak, saya mau tanya tentang pesanan ${o.code}`); nav("/store/chat"); } },
          { icon: Headset, label: "Customer Service Prioritas", testid: "bantuan-cs", href: `https://wa.me/${WA_CS}?text=${encodeURIComponent(`[PRIORITAS] Pesanan ${o.code}`)}` },
        ].map((m, i) => {
          const row = (
            <div className="flex items-center gap-3 py-3">
              <m.icon className="w-5 h-5 text-cocoa-muted" />
              <span className="flex-1 text-sm">{m.label}</span>
              <ChevronRight className="w-4 h-4 text-cocoa-muted" />
            </div>
          );
          return m.href
            ? <a key={i} data-testid={m.testid} href={m.href} target="_blank" rel="noreferrer" className="block border-b border-blush-soft last:border-0">{row}</a>
            : <button key={i} data-testid={m.testid} onClick={m.action} className="w-full text-left border-b border-blush-soft last:border-0">{row}</button>;
        })}
        {(canCancel || canRequestCancel) && (
          <button data-testid="bantuan-cancel" onClick={cancelOrder} className="w-full text-left flex items-center gap-3 py-3 text-red-500">
            <AlertTriangle className="w-5 h-5" />
            <span className="flex-1 text-sm">{canCancel ? "Batalkan Pesanan" : "Ajukan Pembatalan"}</span>
          </button>
        )}
        <div className="text-[10px] text-cocoa-muted pt-2 border-t border-blush-soft mt-1">
          Pesanan yang belum dibayar otomatis dibatalkan jika tidak ada pembayaran dalam 12 jam. Pembatalan pesanan berstatus Diproses memerlukan persetujuan admin.
        </div>
      </div>

      {/* Pembatalan */}
      <div id="cancel-section">
        {cancelMode && canRequestCancel && (
          <div className="bg-white rounded-2xl border border-blush-soft p-4 space-y-2.5">
            <div className="text-xs font-semibold">Alasan Pembatalan <span className="text-red-500">*</span></div>
            <select data-testid="cancel-reason-code" value={cancelReasonCode} onChange={(e) => setCancelReasonCode(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-blush-soft text-xs bg-white mb-2">
              {cancelReasons.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
            {cancelReasonCode === "others" && (
              <textarea data-testid="cancel-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={3} placeholder="Jelaskan alasan pembatalan (mis. salah pilih ukuran, ingin ganti produk)..." className="w-full px-3 py-2 rounded-xl border border-blush-soft text-xs focus:outline-none focus:ring-2 ring-brand" />
            )}
            <div className="text-[10px] text-cocoa-muted">Pesananmu sudah dibayar, jadi pembatalan perlu persetujuan admin. Dana dikembalikan manual setelah disetujui.</div>
            <div className="flex gap-2">
              <button onClick={() => setCancelMode(false)} className="flex-1 h-10 rounded-full border border-blush-soft text-xs">Batal</button>
              <button data-testid="cancel-request-submit" onClick={submitCancelRequest} disabled={busy} className="flex-1 h-10 rounded-full bg-red-500 text-white text-xs font-semibold disabled:opacity-50">{busy ? "Mengirim..." : "Kirim Pengajuan"}</button>
            </div>
          </div>
        )}
        {cr.status === "menunggu_persetujuan" && (
          <div data-testid="cancel-pending-banner" className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 text-xs text-amber-700 flex gap-2">
            <Clock className="w-4 h-4 flex-shrink-0" /> Pengajuan pembatalan sedang ditinjau admin. Alasan: {cr.reason}
          </div>
        )}
        {cr.status === "ditolak" && (
          <div data-testid="cancel-rejected-banner" className="bg-red-50 border border-red-200 rounded-2xl p-3.5 text-xs text-red-700 flex gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" /> Pengajuan pembatalan ditolak{cr.rejected_note ? `: ${cr.rejected_note}` : ""}
          </div>
        )}
        {cr.status === "disetujui" && (
          <div data-testid="cancel-approved-banner" className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3.5 text-xs text-emerald-700 flex gap-2">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> Pembatalan disetujui. {o.refund_note || "Dana dikembalikan manual oleh admin."}
          </div>
        )}
      </div>

      {/* Retur section */}
      <div id="retur-section">
        {returMode && (
          <div className="bg-white rounded-2xl border border-blush-soft p-4 space-y-2.5">
            <div className="text-xs font-semibold">Alasan Retur <span className="text-red-500">*</span></div>
            <textarea data-testid="retur-reason" value={returReason} onChange={(e) => setReturReason(e.target.value)} rows={3} placeholder="Jelaskan alasan retur (mis. ukuran tidak cocok, produk cacat)..." className="w-full px-3 py-2 rounded-xl border border-blush-soft text-xs focus:outline-none focus:ring-2 ring-brand" />
            <div className="text-xs font-semibold">Bukti Foto / Video <span className="text-red-500">*</span></div>
            <button type="button" data-testid="retur-file-btn" onClick={() => returFileRef.current.click()} className="w-full h-20 rounded-xl border-2 border-dashed border-blush-soft flex flex-col items-center justify-center gap-1 text-cocoa-muted">
              <Upload className="w-5 h-5" />
              <span className="text-[11px]">{returFile ? returFile.name : "Foto maks 5MB • Video maks 25MB (MP4/WEBM/MOV)"}</span>
            </button>
            <input ref={returFileRef} data-testid="retur-file" type="file" accept="image/*,video/*" className="hidden" onChange={pickReturFile} />
            <div className="flex gap-2">
              <button onClick={() => setReturMode(false)} className="flex-1 h-10 rounded-full border border-blush-soft text-xs">Batal</button>
              <button data-testid="retur-submit" onClick={submitRetur} disabled={busy} className="flex-1 h-10 rounded-full bg-brand text-white text-xs font-semibold disabled:opacity-50">{busy ? "Mengirim..." : "Kirim Pengajuan"}</button>
            </div>
          </div>
        )}
        {o.order_status === "retur_diajukan" && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 text-xs text-amber-700 flex gap-2">
            <Clock className="w-4 h-4 flex-shrink-0" /> Pengajuan retur sedang ditinjau CS. Alasan: {o.retur_reason}
          </div>
        )}
        {o.order_status === "retur_menunggu_paket" && (
          <div className="bg-white rounded-2xl border border-blush-soft p-4 space-y-2.5">
            <div className="text-xs font-semibold text-emerald-700">✓ Retur disetujui — kirim paket pengembalian</div>
            <div className="bg-blush rounded-xl p-3 text-xs text-cocoa leading-relaxed">
              <b>Alamat pengembalian:</b><br />Gudang Kiyana by Cirebon Cloth, Kota Cirebon, Jawa Barat<br />
              <span className="text-cocoa-muted">Sertakan kode pesanan #{o.code} di dalam/paket.</span>
            </div>
            <div className="text-xs font-semibold">Isi Resi Pengembalian <span className="text-red-500">*</span></div>
            <div className="flex gap-2">
              <select data-testid="retur-resi-courier" value={resiCourier} onChange={(e) => setResiCourier(e.target.value)} className="h-11 px-2 rounded-xl border border-blush-soft text-xs bg-white">
                <option value="jne">JNE</option><option value="spx">SPX</option><option value="jnt">J&T</option><option value="sicepat">SiCepat</option><option value="pos">POS</option>
              </select>
              <input data-testid="retur-resi-awb" value={resiAwb} onChange={(e) => setResiAwb(e.target.value)} placeholder="Nomor resi" className="flex-1 h-11 px-3 rounded-xl border border-blush-soft text-xs font-mono focus:outline-none focus:ring-2 ring-brand" />
            </div>
            <button data-testid="retur-resi-submit" onClick={submitReturResi} className="w-full h-11 rounded-full bg-brand text-white text-xs font-semibold">Kirim Resi Pengembalian</button>
          </div>
        )}
        {o.order_status === "retur_dikirim" && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3.5 text-xs text-blue-700 flex gap-2">
            <Truck className="w-4 h-4 flex-shrink-0" /> Paket retur dalam perjalanan — resi <b className="font-mono">{o.retur_courier} {o.retur_awb}</b>. Refund diproses setelah paket kami terima.
          </div>
        )}
        {o.order_status === "refund_diproses" && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3.5 text-xs text-blue-700 flex gap-2">
            <Clock className="w-4 h-4 flex-shrink-0" /> Paket retur sudah kami terima. Refund {formatIDR(o.total)} sedang diproses.
          </div>
        )}
        {o.order_status === "refund_selesai" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3.5 text-xs text-emerald-700 flex gap-2">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> Refund {formatIDR(o.total)} telah dikembalikan. Terima kasih.
          </div>
        )}
        {o.retur_rejected_reason && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-3.5 text-xs text-red-700 flex gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" /> Retur ditolak: {o.retur_rejected_reason}
          </div>
        )}
      </div>

      {/* No. Pesanan */}
      <div className="bg-white rounded-2xl border border-blush-soft p-4">
        <div className="flex items-center justify-between py-1">
          <span className="text-sm">No. Pesanan</span>
          <span className="flex items-center gap-2">
            <span className="font-mono text-sm">{o.code}</span>
            <button data-testid="copy-order-code" onClick={() => { navigator.clipboard?.writeText(o.code); toast.success("No. pesanan disalin"); }} className="text-[11px] border border-blush-soft rounded-lg px-2.5 py-1 text-cocoa">Salin</button>
          </span>
        </div>
        <div className="flex items-center justify-between py-2.5 border-t border-blush-soft">
          <span className="text-sm text-cocoa-muted">Metode Pembayaran</span>
          <span className="text-sm">{PAY_LABEL[o.payment_method] || o.payment_method}</span>
        </div>
        <button data-testid="order-info-more" onClick={() => setExpandInfo(!expandInfo)} className="w-full flex items-center justify-center gap-1.5 py-2.5 border-t border-blush-soft text-sm text-cocoa-muted">
          Lihat Semua <ChevronDown className={`w-4 h-4 transition ${expandInfo ? "rotate-180" : ""}`} />
        </button>
        {expandInfo && (
          <div className="space-y-1.5 text-xs pb-1 border-t border-blush-soft pt-3">
            <div className="flex justify-between"><span className="text-cocoa-muted">Waktu Pemesanan</span><span>{formatDateID(o.created_at)} {formatTimeID(o.created_at)}</span></div>
            {latestProof && <div className="flex justify-between"><span className="text-cocoa-muted">Waktu Pembayaran</span><span>{formatDateID(latestProof.paid_at)}</span></div>}
            {o.shipped_at && <div className="flex justify-between"><span className="text-cocoa-muted">Waktu Pengiriman</span><span>{formatDateID(o.shipped_at)} {formatTimeID(o.shipped_at)}</span></div>}
            <div className="flex justify-between"><span className="text-cocoa-muted">Kurir</span><span>{si.service_name || o.courier || "-"}</span></div>
            {si.awb && (
              <div className="flex justify-between items-center">
                <span className="text-cocoa-muted">No. Resi</span>
                <span className="flex items-center gap-1.5 font-mono">{si.awb}
                  <button data-testid="copy-awb" onClick={() => { navigator.clipboard?.writeText(si.awb); toast.success("Resi disalin"); }}><Copy className="w-3 h-3 text-brand" /></button>
                </span>
              </div>
            )}
            {o.note && <div className="flex justify-between"><span className="text-cocoa-muted">Catatan</span><span className="text-right max-w-[60%]">{o.note}</span></div>}
          </div>
        )}
      </div>

      {/* Bottom action bar */}
      {(si.awb || needPay) && (
        <div className="fixed bottom-[57px] inset-x-0 z-40 bg-canvas-soft/95 backdrop-blur px-4 py-3 border-t border-blush-soft">
          <div className="max-w-2xl mx-auto">
            {si.awb && !needPay ? (
              <button data-testid="btn-lacak-bottom" onClick={goLacak} className="w-full h-12 rounded-full border-2 border-brand text-brand font-semibold bg-white">Lacak</button>
            ) : (
              <button data-testid="btn-bayar-bottom" onClick={() => scrollTo("payment-section")} className="w-full h-12 rounded-full bg-brand text-white font-semibold">Bayar Sekarang</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
